import * as React from "react"
import { useTranslation } from "react-i18next"
import { cva, type VariantProps } from "class-variance-authority"
import { Check, ChevronsUpDown } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const comboboxVariants = cva([], {
  variants: {
    size: {
      sm: "w-48", // 192px
      md: "w-52", // 208px
      "campaign-filter": "w-campaign-filter",
      lg: "w-96",
      university: "w-university-picker",
      xl: "w-[400px]",
      "2xl": "w-[500px]",
      "3xl": "w-[600px]",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

type Props<T> = {
  customFilterFunction?: (
    value: string,
    search: string,
    keywords: string[] | undefined,
  ) => number
  getOptionKeywords?: (option: T) => string[]
  getOptionValue: (option: T) => string
  onChange: (value: string) => void
  options: T[]
  placeholder?: string
  renderButton: (option: T) => React.ReactNode
  renderOption: (option: T) => React.ReactNode
  size?: VariantProps<typeof comboboxVariants>["size"]
  value: string | null
}

/**
 * A component to select a single value from a list of options displayed in a popover,
 * with a search input to filter the options
 */
export function GenericCombobox<T>({
  customFilterFunction,
  getOptionKeywords,
  getOptionValue,
  onChange,
  options,
  placeholder = "Select option...",
  ref,
  renderButton,
  renderOption,
  size,
  value,
}: { ref?: React.Ref<HTMLButtonElement> } & Props<T>) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)

  // <Command> `onSelect` callback only handles string values
  function handleSelect(selectedValue: string) {
    if (value === selectedValue) {
      return // the selected value cannot be removed
    } else {
      onChange(selectedValue)
      setOpen(false)
    }
  }

  const currentOption =
    value !== null && options.find((option) => getOptionValue(option) === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between font-normal",
            comboboxVariants({ size }),
          )}
        >
          {currentOption ? renderButton(currentOption) : placeholder}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(comboboxVariants({ size }), "p-0")}>
        <Command filter={customFilterFunction}>
          <CommandInput placeholder={t("global.search.action")} />
          <CommandList>
            <CommandEmpty>{t("global.search.noItemsFound")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const optionValue = getOptionValue(option)
                const keywords = getOptionKeywords
                  ? getOptionKeywords(option)
                  : []

                return (
                  <CommandItem
                    key={optionValue}
                    value={optionValue}
                    keywords={keywords}
                    onSelect={handleSelect}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        value === optionValue ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {renderOption(option)}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
